"use client"

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { Send, AtSign, Clock, Square } from "lucide-react"
import { Button } from "@sourcepilot/ui/framework/ui/atoms/button"

import { useSimpleAuth as useAuth } from "@sourcepilot/ui/shell/auth/use-simple-auth"
import useFocus from "@sourcepilot/ui/framework/data/hooks/use-focus"
import { useAtom } from 'jotai'
import { pendingContextItemAtom } from '@sourcepilot/ui/framework/state/session-data';const SOURCEPILOT_SERVER_URL = process.env.NEXT_PUBLIC_SOURCEPILOT_URL
import { QueryNames } from '@sourcepilot/generated/query-names';

interface ContextItem {
  id: string
  name: string
  search_path: string
  _label: string
}

interface SelectedContext {
  id: string
  name: string
  search_path: string
  _label: string
}

// Type colors for badges
const typeColors: Record<string, string> = {
  'PDMProduct': 'bg-blue-100 text-blue-800',
  'PDMAction': 'bg-green-100 text-green-800',
  'PDMRequirement': 'bg-purple-100 text-purple-800',
  'PDMPart': 'bg-orange-100 text-orange-800',
  'PDMSupplyChainCriteria': 'bg-yellow-100 text-yellow-800',
}

export default function ChatInput({
  chatSlug,
  value,
  onSend,
  onValueChange,
  setAutoScroll,
  scrollToBottomRef,
  canAddContext = false,
  onContextSelect,
  isProcessing = false,
  isPaused = false,
  queuedMessage = null,
  contextSearchScopes,
  modelSwitch,
  onStop
}: {
  chatSlug: string | null,
  value?: string | '',
  onSend: (value: string, contextIds?: string[]) => void,
  onValueChange?: () => void,
  setAutoScroll?: (value: boolean) => void,
  scrollToBottomRef?: React.MutableRefObject<(() => void) | null>,
  canAddContext?: boolean,
  onContextSelect?: (context: SelectedContext) => void,
  isProcessing?: boolean,
  isPaused?: boolean,
  queuedMessage?: string | null,
  /** Model switch shown above the input (framework/ui/organisms/model-switch.ts):
   *  the model chosen for this chat (a new chat's is the primary); a
   *  fallback changes it. `notice` says why when a fallback switched it.
   *  Omitted → no switch rendered. */
  modelSwitch?: {
    options: Array<{ preference: 'primary' | 'backup'; label: string; modelName: string }>
    value: 'primary' | 'backup'
    onChange: (value: 'primary' | 'backup') => void
    notice?: string | null
    onOpen?: () => void
  },
  /** Context-search sources for the "@" picker. When omitted the picker
   *  uses the default PDM `search-context-items` query. When more than one
   *  scope is supplied the picker renders a toggle (first = default);
   *  RFLP chats pass a project + workspace pair. Each scope's `params` are
   *  merged into the query's `source` alongside `search_term` / `limit`. */
  contextSearchScopes?: Array<{
    key: string
    label: string
    queryName: string
    params?: Record<string, any>
  }>,
  /** When provided AND isProcessing is true, the Send button is replaced
   *  with a Stop button. Parent typically POSTs to /tasks/{id}/cancel;
   *  the server flips Task.status and the latest Step.is_complete, which
   *  propagates back via DAG patch and naturally clears isProcessing. */
  onStop?: () => void
}) {
  const [internalInput, setInternalInput] = useState(value || '')
  // Map of search_path -> context object (for looking up @mentions)
  const [contextMap, setContextMap] = useState<Map<string, SelectedContext>>(new Map())
  const { user } = useAuth()

  // Derive active contexts by checking which known search_paths appear after @ in text
  const activeContexts = useMemo(() => {
    const contexts: SelectedContext[] = []
    const allPaths = [...contextMap.keys()]

    for (const [searchPath, ctx] of contextMap.entries()) {
      const mention = `@${searchPath}`
      const idx = internalInput.indexOf(mention)
      if (idx !== -1) {
        // Check if a longer path in contextMap also matches at this position
        const hasLongerMatch = allPaths.some(otherPath =>
          otherPath !== searchPath &&
          otherPath.startsWith(searchPath) &&
          internalInput.indexOf(`@${otherPath}`) === idx
        )
        if (!hasLongerMatch && !contexts.find(c => c.id === ctx.id)) {
          contexts.push(ctx)
        }
      }
    }
    return contexts
  }, [internalInput, contextMap])

  // Pending context from external "add to context" buttons
  const [pendingContext, setPendingContext] = useAtom(pendingContextItemAtom)

  // Autocomplete state
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [autocompleteResults, setAutocompleteResults] = useState<ContextItem[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [mentionStart, setMentionStart] = useState<number | null>(null) // Position of @ in text
  const [searchTerm, setSearchTerm] = useState('')

  // Available context-search scopes. Falls back to the default PDM query
  // when the parent doesn't supply RFLP (or other) scopes.
  const scopes = useMemo(
    () =>
      contextSearchScopes && contextSearchScopes.length > 0
        ? contextSearchScopes
        : [{ key: 'default', label: '', queryName: QueryNames.pdmSearchContextItems, params: undefined }],
    [contextSearchScopes]
  )
  const [activeScopeKey, setActiveScopeKey] = useState(scopes[0].key)
  // Keep the active scope valid if the scope set changes (e.g. the project
  // id resolves after the attached entity loads).
  useEffect(() => {
    if (!scopes.some(s => s.key === activeScopeKey)) {
      setActiveScopeKey(scopes[0].key)
    }
  }, [scopes, activeScopeKey])
  const activeScope = scopes.find(s => s.key === activeScopeKey) || scopes[0]

  const [textareaRef] = useFocus<HTMLTextAreaElement>();
  const autocompleteRef = useRef<HTMLDivElement>(null)
  const inputHeight = 34;
  const maxHeight = inputHeight * 8;

  const setHeight = (target: HTMLTextAreaElement) => {
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, maxHeight)}px`;
  }

  const handleInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    setHeight(event.currentTarget)
  };

  // Format path with spaces for display
  const formatPath = (path: string) => path.split('/').join(' / ')

  // Get short type name for badge
  const getTypeBadge = (label: string) => {
    const shortName = label.replace('PDM', '')
    return (
      <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${typeColors[label] || 'bg-gray-100 text-gray-800'}`}>
        {shortName}
      </span>
    )
  }

  // Highlight matching text in bold
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text

    // Normalize query for matching (handle path separators)
    const normalizedQuery = query.replace(/\s*\/\s*/g, '/').toLowerCase()
    const normalizedText = text.toLowerCase()

    // Find match position
    const matchIndex = normalizedText.indexOf(normalizedQuery)
    if (matchIndex === -1) {
      // Try matching individual words
      const words = normalizedQuery.split('/').filter(w => w.trim())
      let result: React.ReactNode[] = []
      let lastIndex = 0
      let currentText = text

      for (const word of words) {
        const wordIndex = currentText.toLowerCase().indexOf(word.toLowerCase())
        if (wordIndex !== -1) {
          if (wordIndex > 0) {
            result.push(currentText.slice(0, wordIndex))
          }
          result.push(<strong key={result.length}>{currentText.slice(wordIndex, wordIndex + word.length)}</strong>)
          currentText = currentText.slice(wordIndex + word.length)
        }
      }
      if (currentText) result.push(currentText)
      return result.length > 0 ? result : text
    }

    return (
      <>
        {text.slice(0, matchIndex)}
        <strong>{text.slice(matchIndex, matchIndex + normalizedQuery.length)}</strong>
        {text.slice(matchIndex + normalizedQuery.length)}
      </>
    )
  }

  // Search for context items (empty term searches all, sorted by path length)
  const searchContextItems = useCallback(async (term: string) => {
    setIsSearching(true)
    try {
      const response = await fetch(`${SOURCEPILOT_SERVER_URL}/api/query`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query_name: activeScope.queryName,
          params: { source: { search_term: term, limit: 10, ...(activeScope.params || {}) } },
          format: 'neograph'
        })
      })

      if (!response.ok) throw new Error(`Search failed: ${response.status}`)

      const data = await response.json()
      const nodes = data.root_nodes || data.nodes || []
      const items: ContextItem[] = nodes.map((node: any) => ({
        id: node.id,
        name: node.name,
        search_path: node.search_path || node.name,
        _label: node._label
      }))
      setAutocompleteResults(items)
      setSelectedIndex(0)
    } catch (error) {
      console.error('Search error:', error)
      setAutocompleteResults([])
    } finally {
      setIsSearching(false)
    }
  }, [activeScope.queryName, JSON.stringify(activeScope.params)])

  // Debounced search (triggers even with empty term to show top-level items)
  useEffect(() => {
    if (!showAutocomplete) {
      return
    }

    const timeoutId = setTimeout(() => {
      searchContextItems(searchTerm)
    }, 200)

    return () => clearTimeout(timeoutId)
  }, [searchTerm, showAutocomplete, searchContextItems])

  // Handle text changes - detect @ mentions
  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    const cursorPos = e.target.selectionStart
    setInternalInput(newValue)

    // Notify parent that value changed (clears failed message restore)
    onValueChange?.()

    // Find if we're in an @ mention
    const textBeforeCursor = newValue.slice(0, cursorPos)
    const lastAtIndex = textBeforeCursor.lastIndexOf('@')

    if (lastAtIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' '
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
        const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1)

        // Don't allow newlines in search term
        if (textAfterAt.includes('\n')) {
          setShowAutocomplete(false)
          setMentionStart(null)
          setSearchTerm('')
          return
        }

        // Don't re-open for an already-completed mention. A selected item
        // is inserted as `@<search_path>`; once the cursor moves past it
        // (the text after `@` equals a known context key, or continues
        // past it with a space) the user is typing a normal message, not
        // composing a mention. Without this, names containing spaces
        // re-trigger the picker on every keystroke after selection.
        const isCompletedMention = [...contextMap.keys()].some(
          key => textAfterAt === key || textAfterAt.startsWith(key + ' ')
        )
        if (isCompletedMention) {
          setShowAutocomplete(false)
          setMentionStart(null)
          setSearchTerm('')
          return
        }

        // If autocomplete is already open, allow spaces (stay open)
        // If not open, only open if no trailing space (fresh @ trigger)
        if (showAutocomplete || !textAfterAt.endsWith(' ')) {
          setMentionStart(lastAtIndex)
          setSearchTerm(textAfterAt)
          setShowAutocomplete(true)
          return
        }
      }
    }

    // Not in a mention
    setShowAutocomplete(false)
    setMentionStart(null)
    setSearchTerm('')
  }

  // Handle selection of autocomplete item
  const selectItem = useCallback((item: ContextItem) => {
    if (mentionStart === null) return

    // Replace @searchTerm with the path
    const beforeMention = internalInput.slice(0, mentionStart)
    const afterMention = internalInput.slice(mentionStart + 1 + searchTerm.length)
    const newText = `${beforeMention}@${item.search_path}${afterMention}`

    setInternalInput(newText)
    setShowAutocomplete(false)
    setMentionStart(null)
    setSearchTerm('')

    // Add to context map (keyed by search_path for lookup)
    setContextMap(prev => new Map(prev).set(item.search_path, item))
    onContextSelect?.(item)

    // Focus back on textarea and set cursor after the inserted path
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStart + 1 + item.search_path.length
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
      }
    }, 0)
  }, [mentionStart, searchTerm, internalInput, onContextSelect, textareaRef])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // an open autocomplete takes Esc, results or none: it closes, and the
    // key goes no further (Esc in the chat stops the turn: esc-to-stop.ts)
    if (showAutocomplete && e.key === 'Escape') {
      e.preventDefault()
      setShowAutocomplete(false)
      return
    }
    if (showAutocomplete && autocompleteResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, autocompleteResults.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
        return
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
        e.preventDefault()
        selectItem(autocompleteResults[selectedIndex])
        return
      }
    }

    // Normal enter to send (when not in autocomplete)
    if (e.key === 'Enter' && !e.shiftKey && !showAutocomplete) {
      e.preventDefault()
      handleSend()
    }
  }

  // Scroll selected item into view
  useEffect(() => {
    const dropdown = autocompleteRef.current
    if (dropdown && autocompleteResults.length > 0) {
      const selectedEl = dropdown.children[selectedIndex] as HTMLElement
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [selectedIndex, autocompleteResults.length])

  const handleSend = () => {
    const trimmedInput = internalInput?.trim();
    // Questions and approvals resume through their cards, not a new message.
    if (isPaused) return;
    if (user && trimmedInput) {
      // Extract context IDs from active contexts
      const contextIds = activeContexts.map(ctx => ctx.id)
      onSend(trimmedInput, contextIds.length > 0 ? contextIds : undefined);

      if (setAutoScroll) {
        setAutoScroll(true);
      }

      setInternalInput('');
      setContextMap(new Map())  // Clear context map for next message
      if (textareaRef.current) {
        textareaRef.current.style.height = `${inputHeight}px`;
      }
    }
  }

  useEffect(() => {
    setInternalInput(value || '');
  }, [value]);

  // Auto-grow the textarea on any value change, including programmatic ones
  // (selecting a mention, pending-context insert, "@" button). `onInput`
  // only fires on user keystrokes, so without this a selection that wraps
  // onto a new line wouldn't resize the box.
  useEffect(() => {
    if (textareaRef.current) setHeight(textareaRef.current);
  }, [internalInput]);

  // Consume pending context from external "add to context" buttons
  // Insert @search_path at cursor (or prepend if not focused)
  useEffect(() => {
    if (pendingContext) {
      // Add to context map
      setContextMap(prev => new Map(prev).set(pendingContext.search_path, pendingContext))

      // Insert @search_path into text
      const mention = `@${pendingContext.search_path} `

      if (textareaRef.current && document.activeElement === textareaRef.current) {
        // Textarea is focused - insert at cursor
        const cursorPos = textareaRef.current.selectionStart
        const before = internalInput.slice(0, cursorPos)
        const after = internalInput.slice(cursorPos)
        const needsSpace = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n')
        setInternalInput(`${before}${needsSpace ? ' ' : ''}${mention}${after}`)
      } else {
        // Not focused - prepend to text
        const needsSpace = internalInput.length > 0 && !internalInput.startsWith(' ')
        setInternalInput(`${mention}${needsSpace ? '' : ''}${internalInput}`)
      }

      onContextSelect?.(pendingContext)
      // Clear the pending context
      setPendingContext(null)
    }
  }, [pendingContext, onContextSelect, setPendingContext, internalInput, textareaRef])

  const insertAtSymbol = () => {
    if (!textareaRef.current) return

    const cursorPos = textareaRef.current.selectionStart
    const before = internalInput.slice(0, cursorPos)
    const after = internalInput.slice(cursorPos)

    // Add space before @ if needed
    const needsSpace = before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n')
    const newText = `${before}${needsSpace ? ' ' : ''}@${after}`

    setInternalInput(newText)

    // Set cursor after @
    const newCursorPos = cursorPos + (needsSpace ? 2 : 1)
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus()
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos)
        // Trigger the autocomplete
        setMentionStart(newCursorPos - 1)
        setSearchTerm('')
        setShowAutocomplete(true)
      }
    }, 0)
  }

  // Fixed height for dropdown to fit ~8 results (each ~44px)
  const dropdownHeight = 352 // 8 * 44px

  return (
    <div className={`w-full py-[5px] relative transition-all duration-150 ${showAutocomplete ? 'pb-[360px]' : ''}`}>
      {/* Model switch — the model chosen for this chat; a fallback changes
          it, and the line under it says why. Only rendered when more than
          one model is available. */}
      {modelSwitch && modelSwitch.options.length > 1 && (
        <div className="mb-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-gray-400">Model</span>
            <select
              aria-label="Model"
              data-testid="chat-model-switch"
              value={modelSwitch.value}
              onChange={e => modelSwitch.onChange(e.target.value as 'primary' | 'backup')}
              onFocus={() => modelSwitch.onOpen?.()}
              className="text-xs text-gray-700 bg-white border border-gray-200 rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-[#00ccaa] cursor-pointer"
            >
              {modelSwitch.options.map(opt => (
                <option key={opt.preference} value={opt.preference}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          {modelSwitch.notice && (
            <p data-testid="chat-model-notice" role="status" className="text-[11px] text-amber-700 mt-1">
              {modelSwitch.notice}
            </p>
          )}
        </div>
      )}

      {/* Queued messages - displayed like user messages but with outline style */}
      {queuedMessage && (
        <div className="flex flex-col items-end mb-2">
          {queuedMessage.split('\n').map((line, index) => (
            line.trim() && (
              <div
                key={index}
                className="p-[5px] bg-white border border-gray-300 rounded-[5px] mr-[2px] mb-1 opacity-70"
              >
                <div className="flex items-center gap-2">
                  <div className="text-gray-600 whitespace-pre-wrap text-sm">{line}</div>
                  <Clock className="w-4 h-4 text-gray-400 flex-shrink-0 animate-pulse" />
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Active contexts chips (read-only, derived from @mentions in text) */}
      {activeContexts.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {activeContexts.map(ctx => (
            <span
              key={ctx.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {getTypeBadge(ctx._label)}
              <span className="truncate max-w-[150px]">{ctx.name}</span>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-1 relative">
        {canAddContext && (
          <Button
            variant="ghost"
            size="icon"
            onClick={insertAtSymbol}
            title="Add context (@)"
          >
            <AtSign className="h-4 w-4 text-gray-600" />
          </Button>
        )}
        <div className="flex-grow relative">
          <textarea
            ref={textareaRef}
            value={internalInput}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            placeholder={isPaused ? "Respond to the pending request above..." : user ? "Type @ to add context..." : "Please login to chat..."}
            style={{
              overflow: textareaRef.current && textareaRef.current.scrollHeight > maxHeight ? 'auto' : 'hidden',
              maxHeight: `${maxHeight}px`
            }}
            className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00ccaa] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            rows={1}
            disabled={!user || isPaused}
            data-testid="chat-composer"
            data-esc-stops-turn=""
          />

          {/* Autocomplete dropdown - below the textarea */}
          {showAutocomplete && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
              {/* Scope toggle (e.g. project vs workspace for RFLP chats).
                  onMouseDown preventDefault keeps the textarea focused so
                  switching scope doesn't close the dropdown. Lives OUTSIDE
                  the ref'd scroll container below so keyboard-nav indexing
                  (autocompleteRef.children[selectedIndex]) stays aligned. */}
              {scopes.length > 1 && (
                <div className="flex gap-1 p-1.5 border-b border-gray-100 bg-gray-50">
                  {scopes.map(s => (
                    <button
                      key={s.key}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => setActiveScopeKey(s.key)}
                      className={`text-xs px-2 py-0.5 rounded transition-colors ${
                        s.key === activeScopeKey
                          ? 'bg-[#00ccaa] text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              <div ref={autocompleteRef} className="h-[352px] overflow-y-auto">
              {isSearching ? (
                <div className="p-3 text-center text-gray-500 text-sm">
                  Searching...
                </div>
              ) : autocompleteResults.length === 0 ? (
                <div className="p-3 text-center text-gray-500 text-sm">
                  No items found{searchTerm.trim() ? ` for "${searchTerm}"` : ''}
                </div>
              ) : (
                autocompleteResults.map((item, index) => (
                  <button
                    key={item.id}
                    onClick={() => selectItem(item)}
                    className={`w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 ${
                      index === selectedIndex ? 'bg-blue-50' : ''
                    }`}
                  >
                    {getTypeBadge(item._label)}
                    <div className="flex-grow min-w-0">
                      <div className="text-sm font-medium truncate">
                        {highlightMatch(item.name, searchTerm)}
                      </div>
                      {item.search_path !== item.name && (
                        <div className="text-xs text-gray-500 truncate">
                          {highlightMatch(formatPath(item.search_path), searchTerm)}
                        </div>
                      )}
                    </div>
                  </button>
                ))
              )}
              </div>
            </div>
          )}
        </div>
        {isProcessing && onStop ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onStop}
            title="Stop generating"
            data-testid="chat-stop"
          >
            <Square className="h-4 w-4 fill-red-500 text-red-500" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleSend}
            disabled={!user || isPaused || !internalInput?.trim()}
            data-testid="chat-send"
          >
            <Send className={`h-4 w-4 ${!user || isPaused || !internalInput?.trim() ? 'text-gray-400' : 'text-black'}`} />
          </Button>
        )}
      </div>
    </div>
  )
}
